import { Image, Page, Text, View } from '@react-pdf/renderer'
import { pdfColors, pdfStyles } from './pdf-theme'

const LOGO_URL = '/logo-ROI365.svg'

// react-pdf doesn't render SVG via <Image>, so we use a PNG fallback if needed.
// We'll prefer a PNG export of the logo to keep things simple. If the project
// only ships SVG, the URL-imported PNG below should be added to /public.
// For now we point at a PNG that should be added at /logo-ROI365.png (fall back
// to brand text if missing — handled by the consumer).

export function PDFHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <View
      fixed
      style={{
        position: 'absolute',
        top: 24,
        left: 36,
        right: 36,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: pdfColors.border,
        paddingBottom: 8,
      }}
    >
      <View>
        <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', color: pdfColors.brandPrimary }}>
          ROI365 · {title}
        </Text>
        {subtitle ? (
          <Text style={{ fontSize: 8, color: pdfColors.textTertiary, marginTop: 2 }}>{subtitle}</Text>
        ) : null}
      </View>
      <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: pdfColors.brandSecondary }}>
        ROI365
      </Text>
    </View>
  )
}

export function PDFFooter() {
  return (
    <View
      fixed
      style={{
        position: 'absolute',
        bottom: 18,
        left: 36,
        right: 36,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 6,
        borderTopWidth: 0.5,
        borderTopColor: pdfColors.border,
      }}
    >
      <Text style={{ fontSize: 7, color: pdfColors.textTertiary }}>
        © CIC Consulting Informatico · ROI365
      </Text>
      <Text
        style={{ fontSize: 7, color: pdfColors.textTertiary }}
        render={({ pageNumber, totalPages }) => `pagina ${pageNumber} de ${totalPages}`}
      />
    </View>
  )
}

interface CoverProps {
  reportTitle: string
  reportDescription: string
  clientName: string
  period?: string
  generatedAt: string
}

export function PDFCoverPage({ reportTitle, reportDescription, clientName, period, generatedAt }: CoverProps) {
  return (
    <Page size="A4" style={pdfStyles.page}>
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          paddingHorizontal: 24,
        }}
      >
        {/* Brand bar */}
        <View
          style={{
            width: 60,
            height: 4,
            backgroundColor: pdfColors.brandSecondary,
            marginBottom: 32,
          }}
        />

        <Text
          style={{
            fontSize: 12,
            color: pdfColors.brandSecondary,
            fontFamily: 'Helvetica-Bold',
            letterSpacing: 2,
            marginBottom: 6,
            textTransform: 'uppercase',
          }}
        >
          ROI365 · Informe
        </Text>
        <Text
          style={{
            fontSize: 32,
            fontFamily: 'Helvetica-Bold',
            color: pdfColors.brandPrimary,
            marginBottom: 12,
          }}
        >
          {reportTitle}
        </Text>
        <Text style={{ fontSize: 11, color: pdfColors.textSecondary, lineHeight: 1.5, marginBottom: 36 }}>
          {reportDescription}
        </Text>

        <View
          style={{
            borderLeftWidth: 3,
            borderLeftColor: pdfColors.brandSecondary,
            paddingLeft: 12,
            marginTop: 18,
          }}
        >
          <Text style={{ fontSize: 8, color: pdfColors.textTertiary, textTransform: 'uppercase', letterSpacing: 1 }}>
            Cliente
          </Text>
          <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: pdfColors.brandPrimary, marginTop: 4 }}>
            {clientName}
          </Text>

          {period ? (
            <>
              <Text
                style={{ fontSize: 8, color: pdfColors.textTertiary, textTransform: 'uppercase', letterSpacing: 1, marginTop: 14 }}
              >
                Periodo
              </Text>
              <Text style={{ fontSize: 11, color: pdfColors.textPrimary, marginTop: 2 }}>{period}</Text>
            </>
          ) : null}

          <Text
            style={{ fontSize: 8, color: pdfColors.textTertiary, textTransform: 'uppercase', letterSpacing: 1, marginTop: 14 }}
          >
            Generado el
          </Text>
          <Text style={{ fontSize: 10, color: pdfColors.textPrimary, marginTop: 2 }}>{generatedAt}</Text>
        </View>
      </View>

      <View
        style={{
          position: 'absolute',
          bottom: 36,
          left: 36,
          right: 36,
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTopWidth: 1,
          borderTopColor: pdfColors.border,
          paddingTop: 12,
        }}
      >
        <Text style={{ fontSize: 9, color: pdfColors.textSecondary }}>
          CIC Consulting Informatico · cic.es
        </Text>
        <Text style={{ fontSize: 9, color: pdfColors.brandSecondary, fontFamily: 'Helvetica-Bold' }}>
          ROI365
        </Text>
      </View>
    </Page>
  )
}

// Suppress unused imports warning for Image (kept for future use when we add raster logo)
void Image
void LOGO_URL
